import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import InstallPrompt from "@/components/InstallPrompt";
import { detectLanguageOnMount } from "@/i18n";

// Single shared QueryClient. Module-singleton is fine — the SSG render runs in
// a fresh Node module graph per build, and on the client it's instantiated
// once on first import.
const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/**
 * Provider stack that used to live inside App.tsx. With vite-react-ssg, every
 * route is a child of this layout via <Outlet/>, so the providers wrap every
 * page. SSR-safe: AuthProvider's session lookup happens in useEffect (client
 * only), QueryClient has no module-scope queries, ThemeProvider's localStorage
 * is window-guarded, Toasters render null until something is queued.
 */
export default function RootLayout() {
  // Switch to the user's stored/detected language after hydration. Initial
  // render uses English on both server and client to keep them identical.
  useEffect(() => {
    detectLanguageOnMount();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <AuthProvider>
            <ScrollToTop />
            <Outlet />
            <InstallPrompt />
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
